import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, BookOpen, ShoppingBag, User, LogOut, Menu, X, ArrowLeft, Book, ClipboardList } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import Seo from "@/components/seo/Seo";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { useLogout } from "@/features/auth/hooks";

const NAV = [
  { to: "/panel", label: "Genel Bakış", icon: LayoutDashboard, end: true },
  { to: "/panel/kurslarim", label: "Kurslarım", icon: BookOpen },
  { to: "/panel/denemeler", label: "Denemeler", icon: ClipboardList },
  { to: "/panel/kitaplarim", label: "Kitaplarım", icon: Book },
  { to: "/panel/siparislerim", label: "Siparişlerim", icon: ShoppingBag },
  { to: "/panel/profil", label: "Profil", icon: User },
];

export default function StudentLayout() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex min-h-screen min-w-0 overflow-x-clip bg-brand-50/40">
      <Seo
        title="Öğrenci Paneli | Mersa Akademi"
        description="Mersa Akademi öğrenci paneli."
        path="/panel"
        noindex
      />
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 w-64 transform border-r border-border bg-white transition-transform lg:static lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-border px-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-700 text-white font-bold">
              M
            </div>
            <span className="font-bold">Mersa Akademi</span>
          </Link>
          <button
            className="lg:hidden text-muted-foreground"
            onClick={() => setMobileOpen(false)}
            aria-label="Menüyü kapat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex flex-col gap-1 p-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-brand-700 text-white"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
          <div className="my-2 border-t border-border" />
          <NavLink
            to="/"
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" />
            Anasayfa
          </NavLink>
          <button
            onClick={() =>
              logout.mutate(undefined, { onSuccess: () => navigate("/") })
            }
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-muted"
          >
            <LogOut className="h-4 w-4" />
            Çıkış Yap
          </button>
        </nav>
      </aside>

      {/* Backdrop for mobile */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main — min-w-0: flex çocuğu uzun başlıklarla sayfayı yatay taşırmaz */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-16 min-w-0 items-center justify-between gap-3 border-b border-border bg-background px-4 lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Menüyü aç"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1" />
          {user && (
            <div className="flex min-w-0 items-center gap-3 text-sm">
              <div className="min-w-0 text-right">
                <div className="truncate font-medium">
                  {user.firstName} {user.lastName}
                </div>
                <div className="truncate text-xs text-muted-foreground">{user.email}</div>
              </div>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700 font-bold">
                {user.firstName?.[0]?.toUpperCase()}
              </div>
            </div>
          )}
        </header>
        <main className="min-w-0 max-w-full flex-1 overflow-x-clip p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
